import { Controller } from "@hotwired/stimulus";

export default class UIDatePickerController extends Controller {
  static targets = [
    "hiddenInput",
    "display",
    "trigger",
    "calendar",
    "monthSelect",
    "yearSelect",
    "prevButton",
    "nextButton",
    "calendarDays",
    "timeField",
  ];

  static values = {
    showTime: Boolean,
    defaultTime: String,
    // When true, the popover shows a "Clear" action so a bound date can be returned to unset (the calendar
    // alone can only swap to another date). placeholder is the trigger label to restore on clear.
    clearable: Boolean,
    placeholder: String,
  };

  connect() {
    this.initializeDates();
    // Small delay only for initial load to let select controllers initialize
    setTimeout(() => this.updateCalendarDisplay(), 10);
    // Only set initial value if we have a selected date
    if (this.selectedDate) {
      this.updateDateTimeValue();
    }
  }

  initializeDates() {
    const existingValue = this.hiddenInputTarget.value;

    if (existingValue?.trim()) {
      // Handle both date and datetime values
      const dateValue = existingValue.includes("T") ? existingValue : existingValue + "T00:00:00";
      this.selectedDate = new Date(dateValue);
      this.currentDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
      this.displayTarget.textContent = this.formatDisplayDate(this.selectedDate);

      // If datetime picker, extract local time from the parsed Date object
      if (this.showTimeValue && this.hasTimeFieldTarget && existingValue.includes("T")) {
        const hours = String(this.selectedDate.getHours()).padStart(2, "0");
        const minutes = String(this.selectedDate.getMinutes()).padStart(2, "0");
        this.timeFieldTarget.value = `${hours}:${minutes}`;
      }
    } else {
      const today = new Date();
      this.currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
      this.selectedDate = null;
    }
  }

  onTimeChange() {
    if (this.showTimeValue) {
      this.updateDateTimeValue();
    }
  }

  updateDateTimeValue() {
    if (!this.selectedDate) return;

    if (!this.showTimeValue) {
      // Standard date picker - just set the date
      const newValue = this.formatDate(this.selectedDate);
      if (this.hiddenInputTarget.value !== newValue) {
        this.hiddenInputTarget.value = newValue;
        this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    // DateTime picker - combine date and time, send with timezone offset
    if (this.hasTimeFieldTarget) {
      const timeValue = this.timeFieldTarget.value || this.defaultTimeValue || "00:00";
      const [hours, minutes] = timeValue.split(":");
      const datetime = new Date(
        this.selectedDate.getFullYear(),
        this.selectedDate.getMonth(),
        this.selectedDate.getDate(),
        parseInt(hours, 10),
        parseInt(minutes, 10),
        0
      );

      const formatter = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "shortOffset",
      });

      const newValue = `${this.formatDate(this.selectedDate)} ${formatter.format(datetime)}`;
      if (this.hiddenInputTarget.value !== newValue) {
        this.hiddenInputTarget.value = newValue;
        this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  storePopoverController() {
    const popoverElement = this.element.querySelector('[data-controller*="ui--popover"]');
    if (popoverElement) {
      this.datePickerPopoverController = this.application.getControllerForElementAndIdentifier(
        popoverElement,
        "ui--popover"
      );
    }
  }

  previousMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.updateCalendarDisplay();
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.updateCalendarDisplay();
  }

  onMonthChange() {
    const monthSelect = this.monthSelectTarget.querySelector('[data-select-target="hiddenInput"]');
    const selectedMonth = parseInt(monthSelect.value) - 1;

    if (!isNaN(selectedMonth) && selectedMonth !== this.currentDate.getMonth()) {
      this.currentDate = new Date(this.currentDate.getFullYear(), selectedMonth, 1);
      this.updateCalendarDisplay(false);
    }
  }

  onYearChange() {
    const yearSelect = this.yearSelectTarget.querySelector('[data-select-target="hiddenInput"]');
    const selectedYear = parseInt(yearSelect.value);

    if (!isNaN(selectedYear) && selectedYear !== this.currentDate.getFullYear()) {
      this.currentDate = new Date(selectedYear, this.currentDate.getMonth(), 1);
      this.updateCalendarDisplay(false);
    }
  }

  selectDate(event) {
    event.preventDefault();
    event.stopPropagation();

    const dateStr = event.currentTarget.dataset.date;
    if (!dateStr) return;

    // On a clearable picker, clicking the day that's already selected toggles it back off (→ unset),
    // which is the intuitive way to deselect from the calendar without hunting for the Clear button.
    if (this.clearableValue && this.selectedDate && dateStr === this.formatDate(this.selectedDate)) {
      this.#clearSelection();
      this.closePopover();
      return;
    }

    this.selectedDate = new Date(dateStr + "T00:00:00");
    this.updateDateTimeValue();
    this.displayTarget.textContent = this.formatDisplayDate(this.selectedDate);

    this.updateCalendarForSelectedDate();
    this.closePopover();
  }

  clear(event) {
    event.preventDefault();
    event.stopPropagation();

    this.#clearSelection();
    this.closePopover();
  }

  #clearSelection() {
    this.selectedDate = null;
    if (this.hiddenInputTarget.value !== "") {
      this.hiddenInputTarget.value = "";
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.displayTarget.textContent = this.placeholderValue || "Pick a date";
    this.renderCalendarDays(this.generateCalendarDaysData());
  }

  updateCalendarForSelectedDate() {
    const selectedMonth = this.selectedDate.getMonth();
    const selectedYear = this.selectedDate.getFullYear();

    if (
      selectedMonth !== this.currentDate.getMonth() ||
      selectedYear !== this.currentDate.getFullYear()
    ) {
      this.currentDate = new Date(selectedYear, selectedMonth, 1);
      this.updateCalendarDisplay();
    } else {
      this.renderCalendarDays(this.generateCalendarDaysData());
    }
  }

  closePopover() {
    if (!this.datePickerPopoverController) {
      this.storePopoverController();
    }
    this.datePickerPopoverController?.hide();
  }

  updateCalendarDisplay(updateSelects = true) {
    if (updateSelects) {
      this.updateSelectElements();
    }

    const calendarDays = this.generateCalendarDaysData();
    this.renderCalendarDays(calendarDays);
  }

  updateSelectElements() {
    const monthName = this.currentDate.toLocaleDateString("en-US", { month: "long" });
    const monthValue = this.currentDate.getMonth() + 1;
    const yearValue = this.currentDate.getFullYear();

    this.updateSelectElement(this.monthSelectTarget, monthValue, monthName);
    this.updateSelectElement(this.yearSelectTarget, yearValue, yearValue);
  }

  updateSelectElement(target, value, displayText) {
    const hiddenInput = target.querySelector('[data-select-target="hiddenInput"]');
    const display = target.querySelector('[data-select-target="display"]');
    const selectElement = target.querySelector('[data-controller*="select"]');

    if (hiddenInput) hiddenInput.value = value;
    if (display) display.textContent = displayText;

    if (selectElement) {
      const selectController = this.application.getControllerForElementAndIdentifier(
        selectElement,
        "select"
      );
      if (selectController) {
        selectController.selectedValue = value.toString();
        selectController.updateCheckmarks();
      }
    }
  }

  generateCalendarDaysData() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();

    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDaysToShow = firstDayOfWeek;
    const currentMonthDays = lastDay.getDate();

    // Calculate minimum weeks needed (avoid unnecessary bottom row)
    const totalDaysNeeded = prevMonthDaysToShow + currentMonthDays;
    const weeksNeeded = Math.ceil(totalDaysNeeded / 7);
    const totalDaysToShow = weeksNeeded * 7;
    const nextMonthDaysToShow = totalDaysToShow - prevMonthDaysToShow - currentMonthDays;

    const calendarDays = [];

    this.addPreviousMonthDays(calendarDays, prevMonth, prevMonthDaysToShow, year, month);
    this.addCurrentMonthDays(calendarDays, currentMonthDays, year, month);
    this.addNextMonthDays(calendarDays, nextMonthDaysToShow, year, month);

    return calendarDays;
  }

  addPreviousMonthDays(calendarDays, prevMonth, count, year, month) {
    for (let i = count; i > 0; i--) {
      const day = prevMonth.getDate() - i + 1;
      calendarDays.push({
        day,
        date: new Date(year, month - 1, day),
        isCurrentMonth: false,
        isPreviousMonth: true,
      });
    }
  }

  addCurrentMonthDays(calendarDays, count, year, month) {
    for (let day = 1; day <= count; day++) {
      calendarDays.push({
        day,
        date: new Date(year, month, day),
        isCurrentMonth: true,
        isPreviousMonth: false,
      });
    }
  }

  addNextMonthDays(calendarDays, count, year, month) {
    for (let day = 1; day <= count; day++) {
      calendarDays.push({
        day,
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
        isPreviousMonth: false,
      });
    }
  }

  renderCalendarDays(calendarDays) {
    const today = new Date();
    this.calendarDaysTarget.innerHTML = calendarDays
      .map((dayInfo) => this.renderDayButton(dayInfo, today))
      .join("");
  }

  renderDayButton(dayInfo, today) {
    const isToday = this.isSameDay(dayInfo.date, today);
    const isSelected = this.selectedDate && this.isSameDay(dayInfo.date, this.selectedDate);
    const classes = this.getDayButtonClasses(dayInfo, isToday, isSelected);

    return `
      <button type="button" 
              class="${classes}"
              data-date="${this.formatDate(dayInfo.date)}"
              data-action="click->ui--date-picker#selectDate">
        ${dayInfo.day}
      </button>
    `;
  }

  getDayButtonClasses(dayInfo, isToday, isSelected) {
    const baseClasses =
      "h-8 w-8 p-0 text-sm font-normal rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

    if (dayInfo.isCurrentMonth) {
      let classes = `${baseClasses} hover:bg-accent hover:text-accent-foreground cursor-pointer`;
      if (isToday) classes += " bg-accent text-accent-foreground font-semibold";
      if (isSelected) classes += " bg-primary text-primary-foreground font-semibold";
      return classes;
    } else {
      return `${baseClasses} text-muted-foreground/50 hover:bg-accent/50 hover:text-accent-foreground cursor-pointer`;
    }
  }

  isSameDay(date1, date2) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(date) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}
